'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva('text-muted-foreground inline-flex items-center justify-center', {
  variants: {
    variant: {
      pill: 'bg-muted h-10 rounded-md p-1',
      segmented: 'bg-muted gap-0.5 rounded-lg p-0.75',
      underline: 'border-border h-10 gap-2 border-b',
    },
  },
  defaultVariants: { variant: 'pill' },
});

const tabsTriggerVariants = cva(
  'ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        pill: 'data-[state=active]:bg-background data-[state=active]:text-foreground rounded-sm px-3 py-1.5 data-[state=active]:shadow-sm',
        segmented:
          'data-[state=active]:bg-card data-[state=active]:text-foreground hover:text-foreground rounded-[9px] px-3.5 py-1.5 text-xs font-[610] data-[state=active]:shadow-[0_1px_2px_hsl(var(--foreground)/0.1)]',
        underline:
          'data-[state=active]:text-primary-strong data-[state=active]:border-primary -mb-px border-b-2 border-transparent px-3 py-2',
      },
    },
    defaultVariants: { variant: 'pill' },
  },
);

interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
